import React from "react";
import { useToast } from '@chakra-ui/react';
import {
    Box,
    Button,
    Text,
    VStack,
    HStack,
    Icon,
    Flex,
    Heading,
    Image,
    Container,
    SimpleGrid
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import {
    FaGavel,
    FaBalanceScale,
    FaHandshake,
    FaPhoneAlt,
    FaArrowRight,
    FaCheckCircle
} from "react-icons/fa";
import { GiScales } from "react-icons/gi";
import Testimonials from "../../components/modals/Testmonials";
import HeroCarousel from "../../components/modals/HeroCarousel";
import Marquee from "../../components/modals/Marquee";
import SEO from "../../components/SEO";
import { useLanguage } from "../../context/LanguageContext";

const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "https://legaltitan.am",
    "logo": "https://legaltitan.am/assets/images/legalLogo.jpg",
    "name": "LegalTitan",
    "sameAs": [
        "https://www.facebook.com/profile.php?id=61557661694303"
    ]
};

const HomePage = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const practiceAreas = [
        {
            icon: FaGavel,
            title: t("home.practiceAreas.0.title"),
            description: t("home.practiceAreas.0.description"),
        },
        {
            icon: FaBalanceScale,
            title: t("home.practiceAreas.1.title"),
            description: t("home.practiceAreas.1.description"),
        },
        {
            icon: FaHandshake,
            title: t("home.practiceAreas.2.title"),
            description: t("home.practiceAreas.2.description"),
        },
    ];

    return (
        <Box maxW="100vw" overflowX="hidden">
            <SEO
                title="LegalTitan - Ձեր իրավաբանական վստահելի գործընկերը"
                description="LegalTitan.am – հուսալի իրավաբանական ծառայություններ Հայաստանում։ Փորձառու իրավաբաններ, պաշտպանություն և խորհրդատվություն տարբեր իրավական ոլորտներում։"
                keywords="իրավաբան, իրավաբանական ծառայություններ, իրավաբանական աջակցություն, իրավական խորհրդատվություն, կորպորատիվ իրավունք, քաղաքացիական իրավունք"
                url="https://legaltitan.am/home"
                image="https://legaltitan.am/assets/images/legalLogo.jpg"
                fbAppId="61557661694303"
                structuredData={structuredData}
            />

            {/* 1. Hero Section */}
            <HeroCarousel />

            {/* 2. Seamless Marquee Ribbon */}
            <Marquee title={t("marquee")} />

            {/* 3. Practice Areas / Services Grid */}
            <Box py={{ base: 14, md: 24 }} bg="#f8fafc">
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Box textAlign="center" maxW="720px" mx="auto" mb={{ base: 12, md: 16 }}>
                        <Heading
                            as="h2"
                            fontSize={{ base: "28px", sm: "36px", md: "42px" }}
                            fontWeight="800"
                            color="#0b132b"
                            letterSpacing="-0.02em"
                            lineHeight="1.2"
                        >
                            {t("home.practiceTitle")}
                        </Heading>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, lg: 8 }}>
                        {practiceAreas.map((area, index) => (
                            <Box
                                key={index}
                                bg="white"
                                p={{ base: 7, md: 8 }}
                                borderRadius="2xl"
                                border="1px solid"
                                borderColor="gray.100"
                                boxShadow="0 10px 30px -5px rgba(11, 19, 43, 0.05)"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                position="relative"
                                overflow="hidden"
                                transition="all 0.3s ease"
                                _hover={{
                                    transform: "translateY(-6px)",
                                    boxShadow: "0 20px 40px -10px rgba(11, 19, 43, 0.12)",
                                    borderColor: "#dfba73",
                                }}
                            >
                                <Box>
                                    <Flex
                                        w={14}
                                        h={14}
                                        borderRadius="xl"
                                        bg="rgba(11, 19, 43, 0.05)"
                                        color="#0b132b"
                                        align="center"
                                        justify="center"
                                        mb={6}
                                        transition="all 0.3s ease"
                                        _groupHover={{ bg: "#0b132b", color: "#dfba73" }}
                                    >
                                        <Icon as={area.icon} fontSize="24px" />
                                    </Flex>

                                    <Heading
                                        as="h3"
                                        fontSize={{ base: "lg", md: "xl" }}
                                        fontWeight="800"
                                        color="#0b132b"
                                        mb={3}
                                        letterSpacing="-0.01em"
                                    >
                                        {area.title}
                                    </Heading>

                                    <Text
                                        fontSize="sm"
                                        color="gray.600"
                                        lineHeight="1.8"
                                        mb={6}
                                    >
                                        {area.description}
                                    </Text>
                                </Box>

                                <Button
                                    variant="outline"
                                    borderColor="#0b132b"
                                    color="#0b132b"
                                    size="sm"
                                    w="fit-content"
                                    borderRadius="lg"
                                    fontWeight="700"
                                    rightIcon={<FaArrowRight />}
                                    _hover={{ bg: "#0b132b", color: "white" }}
                                    onClick={() => navigate('/services')}
                                >
                                    {t("home.btnMore")}
                                </Button>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* 4. Asymmetric "Why Choose Us" Story & Credibility */}
            <Box py={{ base: 14, md: 24 }} bg="white">
                <Container maxW="container.xl" px={{ base: 4, md: 8 }}>
                    <Flex
                        direction={{ base: "column", lg: "row" }}
                        align="center"
                        justify="space-between"
                        gap={{ base: 12, lg: 16 }}
                    >
                        {/* Image with Decorative Backdrop */}
                        <Box flex="1" position="relative" w="100%">
                            <Box
                                position="relative"
                                borderRadius="2xl"
                                overflow="hidden"
                                boxShadow="0 25px 50px -12px rgba(11, 19, 43, 0.25)"
                            >
                                <Image
                                    src="/assets/images/homePage/about_seck.jpg"
                                    alt="Legal Titan Law Firm"
                                    w="100%"
                                    maxH={{ base: "340px", md: "460px" }}
                                    objectFit="cover"
                                    transition="transform 0.5s ease"
                                    _hover={{ transform: "scale(1.03)" }}
                                />
                            </Box>

                            {/* Floating Prestige Badge */}
                            <Box
                                position="absolute"
                                bottom={{ base: "-15px", md: "-20px" }}
                                right={{ base: "10px", md: "20px" }}
                                bg="#0b132b"
                                color="white"
                                p={{ base: 4, md: 5 }}
                                borderRadius="2xl"
                                border="1px solid rgba(223, 186, 115, 0.4)"
                                boxShadow="0 15px 30px rgba(0, 0, 0, 0.3)"
                                maxW="240px"
                            >
                                <HStack spacing={3}>
                                    <Flex w={10} h={10} borderRadius="xl" bg="rgba(223, 186, 115, 0.2)" align="center" justify="center">
                                        <Icon as={GiScales} color="#dfba73" fontSize="20px" />
                                    </Flex>
                                    <Box>
                                        <Text fontWeight="800" fontSize="sm" color="white" lineHeight="1.2">
                                            Legal Titan LLC
                                        </Text>
                                        <Text fontSize="xs" color="gray.400" mt={0.5}>
                                            Armenia
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>
                        </Box>

                        {/* Content & Checklist */}
                        <Box flex="1" w="100%">
                            <Heading
                                as="h2"
                                fontSize={{ base: "26px", sm: "32px", md: "40px" }}
                                fontWeight="800"
                                color="gray.900"
                                letterSpacing="-0.02em"
                                mb={5}
                                lineHeight="1.2"
                            >
                                {t("home.whyChooseUs")}
                            </Heading>

                            <Text color="gray.600" fontSize={{ base: "sm", md: "md" }} lineHeight="1.8" mb={6}>
                                {t("connect.heroText")}
                            </Text>

                            <VStack spacing={4} align="stretch" mb={8}>
                                {[
                                    { title: t("connect.badgeLawyers"), desc: t("about.values.1.description") },
                                    { title: t("connect.badgePrivacy"), desc: t("about.values.3.description") },
                                    { title: t("connect.badgeFast"), desc: t("about.values.0.description") },
                                ].map((item, idx) => (
                                    <HStack key={idx} align="flex-start" spacing={3}>
                                        <Icon as={FaCheckCircle} color="#dfba73" fontSize="18px" mt={1} flexShrink={0} />
                                        <Box>
                                            <Text fontWeight="700" fontSize="sm" color="gray.900">{item.title}</Text>
                                            <Text fontSize="xs" color="gray.500">{item.desc}</Text>
                                        </Box>
                                    </HStack>
                                ))}
                            </VStack>

                            <Button
                                size="lg"
                                bg="#0b132b"
                                color="#dfba73"
                                borderRadius="xl"
                                px={8}
                                _hover={{ bg: "#162044", transform: "translateY(-2px)" }}
                                onClick={() => navigate('/about')}
                                rightIcon={<FaArrowRight />}
                            >
                                {t("home.learnMore")}
                            </Button>
                        </Box>
                    </Flex>
                </Container>
            </Box>

            {/* 5. Testimonials Carousel */}
            <Testimonials />

            {/* 6. Call To Action Banner */}
            <Box py={{ base: 14, md: 20 }} bg="#070d1e" position="relative" overflow="hidden">
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    w="700px"
                    h="350px"
                    bg="radial-gradient(circle, rgba(29, 78, 216, 0.25) 0%, transparent 70%)"
                    pointerEvents="none"
                />

                <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1} textAlign="center">
                    <Heading
                        as="h3"
                        color="white"
                        fontSize={{ base: "24px", sm: "30px", md: "40px" }}
                        fontWeight="800"
                        letterSpacing="-0.02em"
                        maxW="800px"
                        mx="auto"
                        lineHeight="1.25"
                        mb={4}
                    >
                        {t("home.ctaTitle")}
                    </Heading>

                    <Text color="gray.300" fontSize={{ base: "sm", md: "md" }} maxW="600px" mx="auto" mb={8} lineHeight="1.7">
                        {t("home.ctaText")}
                    </Text>

                    <HStack justify="center" spacing={{ base: 3, sm: 5 }} flexWrap="wrap">
                        <Button
                            as="a"
                            href="tel:+37477531309"
                            size={{ base: "md", md: "lg" }}
                            bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                            color="#0b132b"
                            fontWeight="700"
                            borderRadius="xl"
                            px={8}
                            boxShadow="0 10px 25px rgba(223, 186, 115, 0.35)"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "0 15px 30px rgba(223, 186, 115, 0.5)" }}
                            leftIcon={<FaPhoneAlt />}
                            onClick={() => toast({ title: t("home.toastCallTitle"), description: t("home.toastCallDesc"), status: 'success', duration: 6000, isClosable: true })}
                        >
                            {t("home.btnCallNow")}
                        </Button>

                        <Button
                            size={{ base: "md", md: "lg" }}
                            variant="outline"
                            color="white"
                            borderColor="rgba(255, 255, 255, 0.3)"
                            borderRadius="xl"
                            px={8}
                            bg="whiteAlpha.100"
                            backdropFilter="blur(8px)"
                            _hover={{ bg: "whiteAlpha.250", borderColor: "white", transform: "translateY(-2px)" }}
                            onClick={() => navigate('/contact')}
                            rightIcon={<FaArrowRight />}
                        >
                            {t("home.btnSendMessage")}
                        </Button>
                    </HStack>
                </Container>
            </Box>
        </Box>
    );
};

export default HomePage;
